import { useMemo } from 'react';
import { BusinessUnit } from 'data/BusinessUnit';
import { GENERAL_CONSTANTS, STORAGE_KEYS } from 'constants/general';
import { getItems } from 'lib/SessionStorage';

interface UseBusinessUnitBrandFilteringProps {
  selectedBusinessUnit?: BusinessUnit;
  selectedVendorId?: number;
}

interface UseBusinessUnitBrandFilteringReturn {
  brandIdsForFiltering?: number[];
  brandIdsStringForAPI?: string;
  businessUnitsIds: number[];
}

/**
 * Custom hook for calculating brand IDs for filtering based on business unit selection
 *
 * This hook provides consistent business unit filtering logic across products and orders:
 * - For "ALL" business units: Returns all available brand IDs for the selected vendor
 * - For specific business unit selection: Returns the selected business unit's brand ID
 *
 * @param props - Configuration object with business unit and vendor data
 * @returns Brand IDs array and comma-separated string for API calls
 */
export const useBusinessUnitBrandFiltering = ({
  selectedBusinessUnit,
  selectedVendorId
}: UseBusinessUnitBrandFilteringProps): UseBusinessUnitBrandFilteringReturn => {
  // Get all business units from session storage - memoized to avoid repeated reads
  const allBusinessUnits: BusinessUnit[] = useMemo(() => {
    try {
      const units = getItems(STORAGE_KEYS.BUSINESS_UNITS);
      return Array.isArray(units) ? units : [];
    } catch (error) {
      console.error('Failed to load business units from session storage:', error);
      return [];
    }
  }, []); // Only read once on mount

  // Filter business units for the selected vendor
  const vendorBusinessUnits = useMemo(() => {
    if (!selectedVendorId) return [];
    return allBusinessUnits.filter(bu => bu.vendorId === selectedVendorId);
  }, [allBusinessUnits, selectedVendorId]);

  // Calculate brand IDs for filtering
  const brandIdsForFiltering = useMemo(() => {
    const isAllBusinessUnits = !selectedBusinessUnit || selectedBusinessUnit.name === GENERAL_CONSTANTS.ALL;
    
    if (isAllBusinessUnits) {
      const brandIds = vendorBusinessUnits
        .map(bu => bu.brandId)
        .filter((brandId): brandId is number => brandId !== undefined);
      return brandIds.length > 0 ? brandIds : undefined;
    }

    return selectedBusinessUnit.brandId ? [selectedBusinessUnit.brandId] : undefined;
  }, [selectedBusinessUnit, vendorBusinessUnits]);

  // Convert to comma-separated string for API calls
  const brandIdsStringForAPI = useMemo(() => {
    return brandIdsForFiltering?.join(',');
  }, [brandIdsForFiltering]);

  // Get business unit IDs for the vendor
  const businessUnitsIds = useMemo(() => {
    return vendorBusinessUnits
      .map(bu => bu.id)
      .filter((id): id is number => id !== undefined);
  }, [vendorBusinessUnits]);

  return {
    brandIdsForFiltering,
    brandIdsStringForAPI,
    businessUnitsIds
  };
};
